import React, { useEffect, useState } from 'react';

// A side-drawer detail/edit panel. Props:
//   title, onClose,
//   sections: [{ title, rows: [{ label, value, render? }] }]
//   editable: optional object describing an editable form
//     editable = {
//       fields: [{ name, label, type?='text', options? }],
//       initial: {...},
//       onSave: (values) => Promise<void>,
//       onDelete?: () => Promise<void>,
//       deleteLabel?: 'Deactivate'
//     }
//   footer: optional React node for extra actions
const EntityDetailPanel = ({ open, title, onClose, sections = [], editable, footer }) => {
    const [editing, setEditing] = useState(false);
    const [values, setValues] = useState(editable?.initial || {});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setValues(editable?.initial || {});
        setEditing(false);
        setError(null);
    }, [editable?.initial, title]);

    if (!open) return null;

    const handleSave = async () => {
        if (!editable?.onSave) return;
        setBusy(true);
        setError(null);
        try {
            await editable.onSave(values);
            setEditing(false);
        } catch (e) {
            setError(e.message || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!editable?.onDelete) return;
        if (!window.confirm(`${editable.deleteLabel || 'Delete'} this record?`)) return;
        setBusy(true);
        setError(null);
        try {
            await editable.onDelete();
            onClose();
        } catch (e) {
            setError(e.message || 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="detail-overlay" onClick={onClose} />
            <aside className="detail-panel" role="dialog" aria-label={title}>
                <header className="detail-header">
                    <h3>{title}</h3>
                    <button className="detail-close" onClick={onClose} aria-label="Close">×</button>
                </header>

                {error && <div className="detail-error">{error}</div>}

                <div className="detail-body">
                    {sections.map((s, i) => (
                        <section key={i} className="detail-section">
                            {s.title && <h4>{s.title}</h4>}
                            <dl className="detail-grid">
                                {s.rows.map((r, j) => (
                                    <React.Fragment key={j}>
                                        <dt>{r.label}</dt>
                                        <dd>{r.render ? r.render() : (r.value ?? '—')}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                        </section>
                    ))}

                    {editable && editing && (
                        <section className="detail-section">
                            <h4>Edit</h4>
                            <div className="detail-form">
                                {editable.fields.map((f) => (
                                    <div key={f.name} className="detail-form-row">
                                        <label>{f.label || f.name}</label>
                                        {f.type === 'select' ? (
                                            <select
                                                value={values[f.name] ?? ''}
                                                onChange={(e) =>
                                                    setValues({ ...values, [f.name]: e.target.value })
                                                }
                                            >
                                                <option value="">—</option>
                                                {(f.options || []).map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : f.type === 'checkbox' ? (
                                            <input
                                                type="checkbox"
                                                checked={!!values[f.name]}
                                                onChange={(e) =>
                                                    setValues({ ...values, [f.name]: e.target.checked })
                                                }
                                            />
                                        ) : (
                                            <input
                                                type={f.type || 'text'}
                                                value={values[f.name] ?? ''}
                                                onChange={(e) =>
                                                    setValues({ ...values, [f.name]: e.target.value })
                                                }
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <footer className="detail-footer">
                    {footer}
                    {editable && !editing && (
                        <button className="btn-primary" onClick={() => setEditing(true)}>
                            Edit
                        </button>
                    )}
                    {editable && editing && (
                        <>
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setEditing(false);
                                    setValues(editable.initial || {});
                                }}
                                disabled={busy}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleSave}
                                disabled={busy}
                            >
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                        </>
                    )}
                    {editable?.onDelete && !editing && (
                        <button
                            className="btn-danger"
                            onClick={handleDelete}
                            disabled={busy}
                        >
                            {editable.deleteLabel || 'Delete'}
                        </button>
                    )}
                </footer>
            </aside>
        </>
    );
};

export default EntityDetailPanel;
